"use client";

import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { networkingApi } from "@/lib/api/services/networking.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, UserPlus, Star } from "lucide-react";
import Link from "next/link";
import { toast } from "@/components/ui/toast";

interface Suggestion {
  id: string;
  userName: string;
  email: string;
  mutualConnections: number;
  sharedInterests: string[];
}

export default function NetworkSuggestionsPage() {
  const queryClient = useQueryClient();

  const { data: suggestions, isLoading } = useQuery({
    queryKey: ["suggestions"],
    queryFn: async () => {
      const data = await networkingApi.getSuggestions();
      return ((data as { data?: Suggestion[] }).data ?? []) as Suggestion[];
    },
  });

  const connectMutation = useMutation({
    mutationFn: (id: string) => networkingApi.connect(id),
    onSuccess: () => {
      toast({ type: "success", title: "Connected", message: "Connection request sent." });
      queryClient.invalidateQueries({ queryKey: ["suggestions"] });
    },
    onError: () => toast({ type: "error", title: "Error", message: "Could not send request." }),
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/network"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Link>
        </Button>
      </div>

      <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground mb-6">People You May Know</h1>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}><CardContent className="p-4"><div className="h-4 w-full bg-muted/10 rounded animate-pulse" /></CardContent></Card>
          ))}
        </div>
      ) : !suggestions || suggestions.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <UserPlus className="mx-auto h-10 w-10 text-muted-foreground/30" />
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground">No suggestions</h3>
            <p className="mt-1 text-sm text-muted-foreground">Check back later for new suggestions.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {suggestions.map((suggestion) => (
            <Card key={suggestion.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <UserPlus className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">{suggestion.userName}</p>
                    <p className="text-xs text-muted-foreground truncate">{suggestion.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <Star className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="text-xs text-muted-foreground">{suggestion.mutualConnections} mutual connections</span>
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {suggestion.sharedInterests.map((interest) => (
                    <span key={interest} className="h-5 px-2 text-[0.6875rem] font-medium rounded-full border bg-muted/10 text-muted-foreground border-border">{interest}</span>
                  ))}
                </div>
                <Button size="sm" onClick={() => connectMutation.mutate(suggestion.id)} disabled={connectMutation.isPending}>
                  Connect
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
